import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Clock, MapPin, CalendarDays, Euro, Phone, Mail, CheckCircle2 } from "lucide-react";
import { CategoryBadge, StatusBadge } from "./CourseCard";
import { TYPE_LABEL, STATUS, formatDateIT, formatDateLongIT, giornataSchedule } from "../lib/categories";
import { imgUrl } from "../lib/api";

const EMAIL = "formazione@rischio-zero.it";
const PHONE = "0445 043370";

export const CourseModal = ({ course, onClose }) => {
  const editions = (course && course.editions) || [];
  const cover = course && imgUrl(course.image);
  return (
    <AnimatePresence>
      {course && (
        <motion.div
          data-testid="course-modal"
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div
            className="absolute inset-0 backdrop-blur-sm bg-black/40"
            onClick={onClose}
            data-testid="modal-overlay"
          />
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.98 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="relative bg-white rounded-t-3xl sm:rounded-3xl p-6 sm:p-8 max-w-lg w-full shadow-2xl max-h-[92vh] overflow-y-auto hide-scrollbar"
          >
            <button
              data-testid="modal-close-btn"
              onClick={onClose}
              className="absolute top-5 right-5 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-white/90 backdrop-blur text-gray-500 hover:bg-white hover:text-gray-800 transition-colors shadow-sm"
            >
              <X size={18} />
            </button>

            {cover && (
              <div className="-mx-6 -mt-6 sm:-mx-8 sm:-mt-8 mb-5 h-48 overflow-hidden rounded-t-3xl bg-gray-100">
                <img src={cover} alt={course.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2 mb-4 pr-10">
              <CategoryBadge categoryKey={course.category} small />
              <StatusBadge status={course.status} />
              <span className="text-[11px] font-600 uppercase tracking-wide text-gray-400 px-2 py-1 rounded-full bg-gray-50">
                {TYPE_LABEL[course.type]}
              </span>
            </div>

            <h2 className="font-display font-800 text-2xl sm:text-3xl tracking-tight text-[#EA6A0A] leading-tight">
              {course.name}
            </h2>
            <p className="mt-3 text-gray-500 leading-relaxed text-sm sm:text-base">
              {course.description}
            </p>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <InfoTile icon={Clock} label="Durata" value={course.duration_label} />
              <InfoTile icon={Euro} label="Prezzo" value={`€${course.price} + IVA`} />
              <InfoTile icon={MapPin} label="Sede" value={course.location} />
            </div>

            <div className="mt-6">
              <p className="text-xs font-600 uppercase tracking-wide text-gray-400 mb-3">
                Giornate del corso
              </p>
              {editions.length === 0 ? (
                <span className="text-sm text-gray-500">Erogato su richiesta — contattaci per le date.</span>
              ) : (
                <div className="space-y-4" data-testid="modal-editions">
                  {editions.map((ed, ei) => (
                    <div key={ed.id || ei} className="rounded-2xl border border-gray-100 p-4">
                      {editions.length > 1 && (
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-[11px] font-700 uppercase tracking-wide text-[#EA6A0A]">Edizione {ei + 1}</span>
                          <StatusBadge status={ed.status || course.status} />
                        </div>
                      )}
                      <div className="space-y-3">
                        {(ed.giornate || []).map((g, gi) => (
                          <div key={gi} className="flex items-start gap-3">
                            <CheckCircle2 size={17} className="text-[#2FA84F] mt-0.5 shrink-0" />
                            <div>
                              <p className="text-sm font-600 text-[#111827] capitalize">{formatDateLongIT(g.date)}</p>
                              {giornataSchedule(g) && <p className="text-xs text-gray-500 mt-0.5">{giornataSchedule(g)}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a
                data-testid="modal-book-email"
                href={`mailto:${EMAIL}?subject=${encodeURIComponent("Prenotazione corso: " + course.name)}&body=${encodeURIComponent("Buongiorno,\nvorrei prenotare il corso \"" + course.name + "\".\n\nNome e cognome:\nAzienda:\nData preferita:\nNumero partecipanti:\n\nGrazie.")}`}
                className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#EA6A0A] text-white font-600 shadow-[0_8px_24px_-6px_rgba(234,106,10,0.4)] hover:bg-[#C8590A] hover:scale-[1.02] transition-all"
              >
                <Mail size={17} />
                Prenota via email
              </a>
              <a
                data-testid="modal-book-phone"
                href={`tel:+390445043370`}
                className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-white text-[#EA6A0A] font-600 border border-gray-200 hover:bg-gray-50 transition-all"
              >
                <Phone size={17} />
                {PHONE}
              </a>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const InfoTile = ({ icon: Icon, label, value }) => (
  <div className="bg-gray-50 rounded-2xl p-4">
    <div className="flex items-center gap-2 text-gray-400 mb-1">
      <Icon size={15} />
      <span className="text-xs font-600 uppercase tracking-wide">{label}</span>
    </div>
    <p className="text-sm font-600 text-[#111827]">{value}</p>
  </div>
);
